import { useCallback, useEffect, useState } from "react";

import { checklistLibraryApi, organisationApi, sitesApi, usersApi } from "@/lib/api";
import * as qk from "@/lib/queryKeys";

import { useAuth, useClerk, useOrganizationList } from "@clerk/react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { navigate } from "wouter/use-browser-location";

import {
  isOnboardingChecklistsStepEnabled,
  ONBOARDING_CHECKLIST_TEMPLATE_IDS,
} from "./onboardingChecklistConfig";
import { clearOnboardingState, ONBOARDING_STORAGE_KEY } from "./onboardingStorage";

const ALL_ONBOARDING_STEPS = ["organisation", "sites", "team", "checklists", "finish"] as const;
export type OnboardingStep = (typeof ALL_ONBOARDING_STEPS)[number];

/**
 * Active wizard steps. The checklists step is only offered when a curated set
 * of library templates is configured via VITE_ONBOARDING_CHECKLIST_TEMPLATE_IDS;
 * otherwise it is hidden from the wizard entirely.
 */
export const ONBOARDING_STEPS: readonly OnboardingStep[] = isOnboardingChecklistsStepEnabled
  ? ALL_ONBOARDING_STEPS
  : ALL_ONBOARDING_STEPS.filter((step) => step !== "checklists");

/**
 * Maps a persisted step to an active one. A persisted step can be inactive
 * when its feature was disabled after the wizard started (e.g. the checklists
 * step); in that case resume at the nearest preceding active step.
 */
function toActiveStep(step: OnboardingStep): OnboardingStep {
  if (ONBOARDING_STEPS.includes(step)) return step;
  const allIndex = ALL_ONBOARDING_STEPS.indexOf(step);
  for (let i = allIndex - 1; i >= 0; i--) {
    const candidate = ALL_ONBOARDING_STEPS[i];
    if (ONBOARDING_STEPS.includes(candidate)) return candidate;
  }
  return ONBOARDING_STEPS[0];
}

const STORAGE_KEY = ONBOARDING_STORAGE_KEY;

interface PersistedOnboardingState {
  step: OnboardingStep;
  orgId: string | null;
  orgName: string | null;
  orgAddress1?: string | null;
  orgTown?: string | null;
  orgPostcode?: string | null;
  /** Clerk user ID that owns this wizard session. Added for user-scoping; absent in legacy entries. */
  userId?: string;
}

function readPersistedState(userId?: string): PersistedOnboardingState | null {
  if (typeof window === "undefined") return null;
  try {
    const raw = window.sessionStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as PersistedOnboardingState;
    if (!ALL_ONBOARDING_STEPS.includes(parsed.step)) return null;
    // Reject state that explicitly belongs to a different user to prevent
    // one browser tab's signed-in user from inheriting another user's wizard.
    if (userId && parsed.userId && parsed.userId !== userId) return null;
    return { ...parsed, step: toActiveStep(parsed.step) };
  } catch {
    return null;
  }
}

function writePersistedState(state: PersistedOnboardingState): void {
  if (typeof window === "undefined") return;
  window.sessionStorage.setItem(STORAGE_KEY, JSON.stringify(state));
}
/**
 * True when the self-serve onboarding wizard has created an organisation and
 * has not finished yet. Used by transitional pages (e.g. accept-invitation)
 * to send the user back to the wizard instead of into the main app.
 */
export function hasOnboardingOrgInProgress(userId?: string): boolean {
  const state = readPersistedState(userId);
  return state != null && state.orgId != null;
}

/**
 * True when any onboarding wizard state is persisted (regardless of whether
 * an organisation has been created yet). Used to detect incomplete self-serve
 * sign-ups that should be resumed at the wizard rather than sent to the app.
 */
export function hasOnboardingInProgress(userId?: string): boolean {
  return readPersistedState(userId) != null;
}

export interface OnboardingOrgDraft {
  name: string;
  address1?: string;
  town?: string;
  postcode?: string;
}

export interface OnboardingSiteDraft {
  title: string;
  address1?: string;
  postcode?: string;
}

export interface OnboardingInviteDraft {
  name: string;
  email: string;
  role: string;
  accessScope: "organisation" | "site";
  siteIds: number[];
}

export function useOnboardingController() {
  const queryClient = useQueryClient();
  const { setActive } = useClerk();
  const { userId } = useAuth();
  const currentUserId = userId ?? undefined;

  const [step, setStep] = useState<OnboardingStep>(
    () => readPersistedState(currentUserId)?.step ?? "organisation",
  );
  const [orgId, setOrgId] = useState<string | null>(
    () => readPersistedState(currentUserId)?.orgId ?? null,
  );
  const [orgName, setOrgName] = useState<string | null>(
    () => readPersistedState(currentUserId)?.orgName ?? null,
  );
  const [orgAddress1, setOrgAddress1] = useState<string | null>(
    () => readPersistedState(currentUserId)?.orgAddress1 ?? null,
  );
  const [orgTown, setOrgTown] = useState<string | null>(
    () => readPersistedState(currentUserId)?.orgTown ?? null,
  );
  const [orgPostcode, setOrgPostcode] = useState<string | null>(
    () => readPersistedState(currentUserId)?.orgPostcode ?? null,
  );
  const [invitedEmails, setInvitedEmails] = useState<string[]>([]);
  const [publishedCount, setPublishedCount] = useState(0);

  useEffect(() => {
    writePersistedState({
      step,
      orgId,
      orgName,
      orgAddress1,
      orgTown,
      orgPostcode,
      userId: currentUserId,
    });
  }, [step, orgId, orgName, orgAddress1, orgTown, orgPostcode, currentUserId]);

  // Recovery: when the wizard has no orgId (e.g. sessionStorage was cleared after
  // org creation) but the user already has a Clerk org membership, skip to the
  // sites step using the existing org so the user is not left on the organisation
  // step where the server would reject a duplicate create with 403.
  const { isLoaded: membershipsLoaded, userMemberships } = useOrganizationList({
    userMemberships: { infinite: false },
  });
  const firstMembershipOrgId = membershipsLoaded
    ? (userMemberships.data?.[0]?.organization.id ?? null)
    : null;
  const firstMembershipOrgName = membershipsLoaded
    ? (userMemberships.data?.[0]?.organization.name ?? null)
    : null;

  useEffect(() => {
    if (orgId !== null || firstMembershipOrgId === null) return;
    setOrgId(firstMembershipOrgId);
    setOrgName(firstMembershipOrgName);
    setStep("sites");
  }, [orgId, firstMembershipOrgId, firstMembershipOrgName]);

  const resolvedOrgId = orgId ?? firstMembershipOrgId;

  const sitesQuery = useQuery({
    queryKey: qk.sites.list(resolvedOrgId ?? "__onboarding__"),
    queryFn: () => sitesApi.list({ orgId: resolvedOrgId ?? undefined }),
    enabled: resolvedOrgId != null,
  });

  const libraryQuery = useQuery({
    queryKey: qk.checklistLibrary.onboardingModelTemplates(ONBOARDING_CHECKLIST_TEMPLATE_IDS),
    queryFn: () =>
      checklistLibraryApi.listModelTemplates({ ids: ONBOARDING_CHECKLIST_TEMPLATE_IDS }),
    enabled: orgId != null && step === "checklists" && isOnboardingChecklistsStepEnabled,
    staleTime: 5 * 60 * 1000,
  });

  const createOrganisation = useMutation({
    mutationFn: async (draft: OnboardingOrgDraft) => {
      const res = await organisationApi.create({
        name: draft.name,
        address1: draft.address1 || null,
        town: draft.town || null,
        postcode: draft.postcode || null,
      });
      return res.organisation;
    },
    onSuccess: (organisation) => {
      // Deliberately do NOT call Clerk's setActive here: activating the org
      // mid-wizard triggers Clerk's session/task navigation (taskUrls) and
      // ejects the user from the wizard. All wizard API calls pass the orgId
      // explicitly, so activation can safely wait until the finish step.
      writePersistedState({
        step: "sites",
        orgId: organisation.id,
        orgName: organisation.name,
        orgAddress1: organisation.address1,
        orgTown: organisation.town,
        orgPostcode: organisation.postcode,
        userId: currentUserId,
      });
      setOrgId(organisation.id);
      setOrgName(organisation.name);
      setOrgAddress1(organisation.address1 ?? null);
      setOrgTown(organisation.town ?? null);
      setOrgPostcode(organisation.postcode ?? null);
      setStep("sites");
      queryClient.invalidateQueries({ queryKey: qk.sites.list(organisation.id) });
    },
  });

  const invalidateSites = useCallback(() => {
    queryClient.invalidateQueries({
      queryKey: qk.sites.list(resolvedOrgId ?? "__onboarding__"),
    });
  }, [queryClient, resolvedOrgId]);

  const createSite = useMutation({
    mutationFn: (draft: OnboardingSiteDraft) => {
      if (!resolvedOrgId) throw new Error("Organisation has not been created yet");
      return sitesApi.create({
        title: draft.title,
        address1: draft.address1 || undefined,
        postcode: draft.postcode || undefined,
        orgId: resolvedOrgId,
      });
    },
    onSuccess: invalidateSites,
  });

  const updateSite = useMutation({
    mutationFn: ({ siteId, draft }: { siteId: number; draft: OnboardingSiteDraft }) => {
      if (!resolvedOrgId) throw new Error("Organisation has not been created yet");
      return sitesApi.update(siteId, {
        title: draft.title,
        address1: draft.address1 || undefined,
        postcode: draft.postcode || undefined,
        orgId: resolvedOrgId,
      });
    },
    onSuccess: invalidateSites,
  });

  const removeSite = useMutation({
    mutationFn: (siteId: number) => sitesApi.remove(siteId),
    onSuccess: invalidateSites,
  });

  const inviteUser = useMutation({
    mutationFn: (draft: OnboardingInviteDraft) => {
      if (!orgId) throw new Error("Organisation has not been created yet");
      return usersApi.create({
        name: draft.name,
        email: draft.email,
        role: draft.role,
        organisationId: orgId,
        accessScope: draft.accessScope,
        siteIds: draft.accessScope === "site" ? draft.siteIds : [],
      });
    },
    onSuccess: (_data, draft) => {
      setInvitedEmails((prev) => [...prev, draft.email]);
      queryClient.invalidateQueries({
        queryKey: qk.organisations.clerkApiInvitations(orgId ?? undefined),
      });
    },
  });

  const publishTemplates = useMutation({
    mutationFn: async (modelTemplateIds: number[]) => {
      if (!orgId) throw new Error("Organisation has not been created yet");
      const settled = await Promise.allSettled(
        modelTemplateIds.map((modelTemplateId) =>
          checklistLibraryApi.publishModelTemplate({ modelTemplateId, orgId }),
        ),
      );
      const fulfilled = settled
        .filter(
          (r): r is PromiseFulfilledResult<Awaited<ReturnType<typeof checklistLibraryApi.publishModelTemplate>>> =>
            r.status === "fulfilled",
        )
        .map((r) => r.value);
      if (fulfilled.length === 0 && modelTemplateIds.length > 0) {
        throw new Error("Failed to publish any checklist templates. Please try again.");
      }
      return fulfilled;
    },
    onSuccess: (results) => {
      setPublishedCount(results.length);
      queryClient.invalidateQueries({ queryKey: qk.checklists.templatesPrefix(orgId ?? undefined) });
      setStep("finish");
    },
  });

  const stepIndex = ONBOARDING_STEPS.indexOf(step);

  const goNext = useCallback(() => {
    setStep((current) => {
      const index = ONBOARDING_STEPS.indexOf(current);
      return ONBOARDING_STEPS[Math.min(index + 1, ONBOARDING_STEPS.length - 1)];
    });
  }, []);

  const goBack = useCallback(() => {
    setStep((current) => {
      const index = ONBOARDING_STEPS.indexOf(current);
      // The organisation step cannot be revisited once the org exists.
      return ONBOARDING_STEPS[Math.max(index - 1, 1)];
    });
  }, []);

  const finish = useCallback(async () => {
    // Clear the wizard state before activating the org so any Clerk-driven
    // navigation (e.g. through the accept-invitation task URL) no longer
    // treats the wizard as in progress.
    clearOnboardingState();
    if (orgId) {
      try {
        // Activate the new organisation as the user leaves the wizard. The
        // no-op navigate keeps Clerk from redirecting; we navigate ourselves.
        await setActive({ organization: orgId, navigate: () => undefined });
      } catch {
        // Best effort — the app shell still lets the user switch organisations.
      }
    }
    navigate("/client-home");
  }, [orgId, setActive]);

  const sites = sitesQuery.data?.sites ?? [];
  const defaultSiteDraft: OnboardingSiteDraft | null =
    sites.length === 0 && orgName
      ? {
          title: orgName,
          address1: orgAddress1 ?? undefined,
          postcode: orgPostcode ?? undefined,
        }
      : null;

  return {
    step,
    stepIndex,
    orgId,
    orgName,
    sites,
    defaultSiteDraft,
    sitesLoading: sitesQuery.isLoading,
    modelTemplates: libraryQuery.data?.modelTemplates ?? [],
    modelTemplatesLoading: libraryQuery.isLoading,
    invitedEmails,
    publishedCount,
    createOrganisation,
    createSite,
    updateSite,
    removeSite,
    inviteUser,
    publishTemplates,
    goNext,
    goBack,
    finish,
  };
}

export type OnboardingController = ReturnType<typeof useOnboardingController>;
